import { useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch } from './client.js';
import type { CoachVideo } from './lesson-videos.js';

/**
 * The videos on a child's lesson, for the parent (step 7b, canvas row 4,
 * approved 17 October): the child's with their notes and the coach's replies,
 * and the coach's own, read only. The parent may remove any of the child's.
 * Links work for an hour; the list is read again after every removal.
 */

type GetToken = () => Promise<string | null>;

export interface FamilyVideo {
  id: string;
  sent_at: string;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  video_url: string;
  opened_at: string | null;
  athlete_note: string | null;
  coach_reply: string | null;
}

export interface FamilyLessonVideos {
  videos: FamilyVideo[];
  coach_videos: CoachVideo[];
}

export type FamilyLessonVideosState = { status: 'loading' } | { status: 'ready'; data: FamilyLessonVideos } | { status: 'failed' };

const base = (lessonId: string) => `/api/lessons/${encodeURIComponent(lessonId)}/videos`;

export function removeFamilyVideo(getToken: GetToken, lessonId: string, videoId: string): Promise<void> {
  return apiFetch(`${base(lessonId)}/${encodeURIComponent(videoId)}`, getToken, { method: 'DELETE' });
}

export function useFamilyLessonVideos(lessonId: string, nonce: number): FamilyLessonVideosState {
  const getToken = useAccessToken();
  const [state, setState] = useState<FamilyLessonVideosState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    apiFetch<FamilyLessonVideos>(base(lessonId), getToken)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, lessonId, nonce]);

  return state;
}
